import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LayersIcon, ZapIcon, TargetIcon, RefreshCwIcon, CheckCircle2Icon, KeyIcon, XIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const STEPS = [{
  num: '01',
  title: 'Leverage existing assets',
  text: 'Approved materials become interactive Snaks — no new content creation.',
  icon: LayersIcon,
  // 12 o'clock
  position: 'left-1/2 top-[17%] -translate-x-1/2 -translate-y-1/2 w-[max(40%,14rem)]'
}, {
  num: '02',
  title: 'Rapid local activation',
  text: 'Centrally built, locally activated. Live in days, not months.',
  icon: ZapIcon,
  // 3 o'clock
  position: 'left-[83%] top-1/2 -translate-x-1/2 -translate-y-1/2 w-[max(34%,12rem)]'
}, {
  num: '03',
  title: 'Behavioural data capture',
  text: 'Real HCP engagement signals, not vanity metrics.',
  icon: TargetIcon,
  // 6 o'clock
  position: 'left-1/2 top-[83%] -translate-x-1/2 -translate-y-1/2 w-[max(40%,14rem)]'
}, {
  num: '04',
  title: 'Continuous optimization',
  text: 'Every interaction sharpens the next campaign — and every market learns.',
  icon: RefreshCwIcon,
  // 9 o'clock
  position: 'left-[17%] top-1/2 -translate-x-1/2 -translate-y-1/2 w-[max(34%,12rem)]'
}];
const DELIVERS = ['Snakkerfy the approved content', 'Localize per market', 'Build the campaign logic', 'Publish and distribute', 'Track engagement', 'Report and optimize'];
export function SlideLoopModel({}: SlideProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  return <InteriorSlide data-id="element-478" data-mp="17f6omq9i011u">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="text-center flex-shrink-0" data-id="element-479" data-mp="17grh8ac959x2">
        <p className="text-[var(--brand)] text-[clamp(0.7rem,1vw,0.9rem)] font-semibold uppercase tracking-[0.18em] mb-2" data-id="element-480" data-mp="e38k244w3kqg">
          The Operating Model
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(1.75rem,3.2vw,2.9rem)] leading-tight tracking-tight" data-id="element-481" data-mp="izfhf8j7znws">
          A loop, not a project
        </h2>
        <p className="text-[var(--muted)] text-[clamp(0.85rem,1.2vw,1.05rem)] mt-2" data-id="element-482" data-mp="ruwvs6b8876w">
          Each campaign cycle feeds the next — across every brand and market.
        </p>
        <button type="button" onClick={() => setDetailsOpen(true)} aria-expanded={detailsOpen} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-lg shadow-[var(--brand)]/25 transition-transform hover:scale-[1.03]" data-id="element-483" data-mp="11ri34yapxudw">
          <KeyIcon className="h-4 w-4" data-id="element-484" data-mp="38l3rnt3o5rze" />
          Turn-key
        </button>
      </motion.div>

      {/* Centered loop */}
      <div className="flex-1 min-h-0 flex items-center justify-center py-2" data-id="element-485" data-mp="350ju3o0jz2rv">
        <div className="relative h-full shrink-0" style={{
        aspectRatio: '1 / 1',
        width: 'auto',
        maxWidth: '100%'
      }} data-id="element-486" data-mp="352o1ifxnujww">
          {/* Visible circular path — cards sit on it at 12, 3, 6 and 9 o'clock */}
          <div className="absolute inset-[17%] rounded-full border-[3px] border-[var(--brand)]/25" data-id="element-487" data-mp="2rb9k2djeiqry" />
          <motion.div className="absolute inset-[17%]" animate={{
          rotate: 360
        }} transition={{
          repeat: Infinity,
          duration: 10,
          ease: 'linear'
        }} data-id="element-488" data-mp="3nywanutsnexh">
            <span className="absolute top-0 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--brand)] shadow-[0_0_0_8px_rgba(243,102,51,0.18)]" data-id="element-489" data-mp="39z4srj05pee7" />
          </motion.div>

          {/* Center badge — dead centre of the circle */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5,
          duration: 0.5
        }} className="absolute top-1/2 left-1/2 z-10 w-[26%] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[var(--brand)]/40 bg-white px-4 py-3 text-center shadow-sm" data-id="element-490" data-mp="3mhfi2upqfau1">
            <p className="text-[var(--brand)] text-[0.55rem] font-bold uppercase tracking-[0.18em]" data-id="element-491" data-mp="e1nhejbpjfb6">
              Compounding
            </p>
            <p className="text-[var(--fg)] font-bold text-[0.8rem] leading-tight mt-0.5" data-id="element-492" data-mp="t8lzkqzcbz">
              Every cycle gets sharper
            </p>
          </motion.div>

          {/* Step cards around the loop */}
          {STEPS.map((step, i) => {
          const Icon = step.icon;
          return <motion.div key={step.num} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3 + i * 0.12,
            duration: 0.5
          }} style={{
            left: step.left,
            top: step.top,
            width: step.width,
            transform: 'translate(-50%, -50%)'
          }} className="absolute flex flex-col rounded-2xl border border-black/5 bg-white p-3.5 text-left shadow-lg" data-id="element-493" data-mp="2pry4zuof6mib" data-mpi={i}>
                <div className="flex items-center gap-2 mb-1.5" data-id="element-494" data-mp="28bkkcwnlwcr8">
                  <span className="h-8 w-8 flex-shrink-0 rounded-xl bg-[var(--surface)] flex items-center justify-center text-[var(--brand)]" data-id="element-495" data-mp="23hzk0xyjj6ov">
                    <Icon className="h-4 w-4" data-id="element-496" data-mp="1fqu6wqjzisi9" />
                  </span>
                  <span className="text-[var(--brand)] text-xs font-bold" data-id="element-497" data-mp="1attxq6ru99mx">
                    {step.num}
                  </span>
                  <h3 className="text-[var(--fg)] font-bold text-[0.9rem] leading-tight" data-id="element-498" data-mp="3fvg4b6vg5xln">
                    {step.title}
                  </h3>
                </div>
                <p className="text-[var(--muted)] text-xs leading-snug" data-id="element-499" data-mp="3kmy6fy97i487">
                  {step.text}
                </p>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Turn-key detail overlay */}
      <AnimatePresence data-id="element-500" data-mp="2wwk8xs7urd68">
        {detailsOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={() => setDetailsOpen(false)} className="absolute inset-0 z-40 bg-black/30 backdrop-blur-sm" data-id="element-501" data-mp="2wwu0vlcgjuek" />
            <motion.div role="dialog" aria-label="Turn-key delivery" initial={{
          opacity: 0,
          y: 16,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 16,
          scale: 0.97
        }} transition={{
          duration: 0.28,
          ease: [0.23, 1, 0.32, 1]
        }} className="absolute top-1/2 left-1/2 z-50 w-[min(56rem,88%)] -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-black/5 bg-white p-[clamp(1.5rem,3vw,2.5rem)] shadow-2xl" data-id="element-502" data-mp="vbhps13sukjo">
              <button type="button" onClick={() => setDetailsOpen(false)} aria-label="Close" className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted)] transition-colors hover:bg-[var(--surface)]" data-id="element-503" data-mp="1x01wzqjjzuol">
                <XIcon className="h-5 w-5" data-id="element-504" data-mp="2g80e19sfekdi" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-505" data-mp="22hwyf1o1xox1">
                <div className="rounded-2xl border border-[var(--brand)]/30 bg-[var(--surface)] p-5" data-id="element-506" data-mp="22fkjyyhi5woq">
                  <h3 className="text-[var(--brand)] text-[0.7rem] font-bold uppercase tracking-[0.16em] mb-3" data-id="element-507" data-mp="27encjjgaqaft">
                    Snakker delivers, end to end
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-508" data-mp="1xjvgik2d3vn5">
                    {DELIVERS.map(item => <li key={item} className="flex items-start gap-2 rounded-xl bg-white/80 px-3 py-2 text-[var(--fg)] text-[13px] font-medium leading-snug" data-id="element-509" data-mp="1jl5kl7ra420d">
                        <CheckCircle2Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--brand)]" data-id="element-510" data-mp="3iwgdjll5xoum" />
                        {item}
                      </li>)}
                  </ul>
                </div>

                <div className="flex flex-col justify-center" data-id="element-511" data-mp="3o21y4djhnoxf">
                  <h3 className="text-[var(--muted)] text-[0.7rem] font-bold uppercase tracking-[0.16em] mb-3" data-id="element-512" data-mp="5nay5a6l9a48">
                    The affiliate only
                  </h3>
                  <ul className="space-y-2 mb-4" data-id="element-513" data-mp="u81gt6ilkjt">
                    {['Reviews and signs off', 'Says go'].map(item => <li key={item} className="flex items-center gap-3 text-[var(--fg)] text-[15px] font-semibold" data-id="element-514" data-mp="amdu854lc602">
                        <span className="h-2 w-2 rounded-full bg-[var(--accent)]" data-id="element-515" data-mp="kkbhapaprwr3" />
                        {item}
                      </li>)}
                  </ul>
                  <p className="border-t border-black/5 pt-3 text-[13px] leading-snug text-[var(--muted)]" data-id="element-516" data-mp="2mberjdgoz9b4">
                    No brief to write, no local project, no vendor to onboard, no
                    hours to find.
                  </p>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </InteriorSlide>;
}